import { AlertCircle, StarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { Grid } from '@radix-ui/themes';
import { Button, EmptyState, ErrorState, Flex, LoadingState, Text } from '@/components/common';
import { useCurrentUser } from '@/contexts';
import { sendActivityHeartbeat } from '@/hooks/useActivityHeartbeat';
import { useUsers } from '@/hooks/useUsers';
import { useFilterStore } from '@/stores/filterStore';
import UserCard from './UserCard';

export function DiscoveryGrid() {
  const { t } = useTranslation('discover');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { user, currentUser: profile, settings } = useCurrentUser();
  const { resetFilters } = useFilterStore();
  const favoritesOnly = useFilterStore(s => s.favoritesOnly);
  const setFavoritesOnly = useFilterStore(s => s.setFavoritesOnly);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { users, isLoading, isError, refetch, hasMore, isLoadingMore, loadMore } = useUsers(
    user,
    profile
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      await sendActivityHeartbeat(user, settings?.locationAllowed ?? false);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isError) {
    return (
      <ErrorState action={{ label: tCommon('actions.retry'), onClick: () => void refetch() }} />
    );
  }

  if (isLoading) {
    return <LoadingState />;
  }

  if (users.length === 0) {
    if (favoritesOnly) {
      return (
        <EmptyState
          icon={<StarIcon color="gray" size={48} strokeWidth={1.5} />}
          title={t('favoritesEmpty.title')}
          description={t('favoritesEmpty.description')}
          action={{
            label: t('favoritesEmpty.action'),
            onClick: () => setFavoritesOnly(false),
          }}
        />
      );
    }
    return (
      <EmptyState
        icon={<AlertCircle color="gray" size={48} strokeWidth={1.5} />}
        title={t('emptyState.title')}
        description={t('emptyState.description')}
        action={{ label: t('emptyState.clear'), onClick: resetFilters }}
      />
    );
  }

  return (
    <Flex direction="column" gap="4">
      {favoritesOnly && (
        <Text as="p" size="1" align="center" color="gray">
          {t('favoritesNote')}
        </Text>
      )}
      <Grid columns={{ initial: '3', md: '6' }} gap="1" mb="4">
        {users.map(user => (
          <UserCard
            key={user.uid}
            user={user}
            currentUser={profile}
            onClick={() => navigate(`/profile/${user.uid}`, { state: { user } })}
          />
        ))}
      </Grid>
      {hasMore ? (
        <Button
          color="gray"
          variant="outline"
          fullWidth
          onClick={() => loadMore()}
          isLoading={isLoadingMore}
        >
          {t('loadMore')}
        </Button>
      ) : (
        <Text as="p" align="center" color="gray">
          {t('allLoaded')}
        </Text>
      )}
      <Button
        color="gray"
        variant="outline"
        fullWidth
        onClick={() => void handleRefresh()}
        isLoading={isRefreshing}
      >
        {t('refreshApp')}
      </Button>
    </Flex>
  );
}
